import React, { useState } from "react";
import { useApi } from "../hooks/useApi";
import { useSSERefetch } from "../hooks/useSSERefetch";

interface HostRaw {
  id: string;
  hostname: string;
  ip_address: string;
  status: "online" | "draining" | "offline";
  device_counts: {
    total: number;
    available: number;
    busy: number;
    offline: number;
  };
}

interface Host {
  id: string;
  hostname: string;
  ip: string;
  status: "online" | "draining" | "offline";
  deviceCount: number;
  availableCount: number;
  busyCount: number;
  offlineCount: number;
}

interface HostsResponse {
  hosts: HostRaw[];
}

function mapHost(h: HostRaw): Host {
  return {
    id: h.id,
    hostname: h.hostname,
    ip: h.ip_address,
    status: h.status,
    deviceCount: h.device_counts?.total ?? 0,
    availableCount: h.device_counts?.available ?? 0,
    busyCount: h.device_counts?.busy ?? 0,
    offlineCount: h.device_counts?.offline ?? 0,
  };
}

export default function HostManagement() {
  const { data, loading, error, refetch } = useApi<HostsResponse>("/api/hosts");
  useSSERefetch("/api/events", ["host:heartbeat", "device:status", "device:claimed", "device:released"], refetch);
  const hosts = (data?.hosts ?? []).map(mapHost);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newHostname, setNewHostname] = useState("");
  const [newIp, setNewIp] = useState("");

  const handleAddHost = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await fetch("/api/hosts", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hostname: newHostname, ip: newIp }),
      });
      setNewHostname("");
      setNewIp("");
      setShowAddForm(false);
      refetch();
    } catch {
      // Error handling could be improved
    }
  };

  const handleAction = async (hostId: string, action: "drain" | "remove") => {
    try {
      await fetch(`/api/hosts/${hostId}/${action}`, {
        method: "POST",
        credentials: "include",
      });
      refetch();
    } catch {
      // Error handling could be improved
    }
  };

  if (loading && !hosts) {
    return <p style={{ padding: "var(--space-xl)" }}>Loading hosts...</p>;
  }

  if (error) {
    return (
      <p style={{ padding: "var(--space-xl)", color: "var(--color-error)" }}>
        Error: {error}
      </p>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "var(--space-xl)",
        }}
      >
        <h1 style={pageTitleStyle}>Host Management</h1>
        <button onClick={() => setShowAddForm(!showAddForm)} style={buttonStyle}>
          {showAddForm ? "Cancel" : "+ Add Host"}
        </button>
      </div>

      {/* Add Host Form */}
      {showAddForm && (
        <form
          onSubmit={handleAddHost}
          style={{
            background: "var(--color-surface)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-card)",
            padding: "var(--space-lg)",
            marginBottom: "var(--space-xl)",
            display: "flex",
            gap: "var(--space-md)",
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: "var(--space-xs)" }}>
            <span style={labelStyle}>Hostname</span>
            <input
              type="text"
              value={newHostname}
              onChange={(e) => setNewHostname(e.target.value)}
              placeholder="mini-05"
              required
              style={inputStyle}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "var(--space-xs)" }}>
            <span style={labelStyle}>IP Address</span>
            <input
              type="text"
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
              placeholder="192.168.1.50"
              required
              style={inputStyle}
            />
          </label>
          <button type="submit" style={{ ...buttonStyle, background: "var(--color-success)" }}>
            Add
          </button>
        </form>
      )}

      {/* Host Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: "var(--space-lg)",
        }}
      >
        {(hosts ?? []).map((host) => (
          <HostCard key={host.id} host={host} onAction={handleAction} />
        ))}
        {hosts?.length === 0 && (
          <p style={{ color: "var(--color-text-secondary)" }}>No hosts configured.</p>
        )}
      </div>

      {/* Setup guide prompt */}
      <SetupGuideSection />
    </div>
  );
}

const INSTALL_URL = `${typeof window !== "undefined" ? window.location.origin : "https://device-hive.ops.gigsmart.com"}/install`;
const INSTALL_CMD = `curl -fsSL ${INSTALL_URL} | bash`;

function SetupGuideSection() {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyInstall = async () => {
    try {
      await navigator.clipboard.writeText(INSTALL_CMD);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback: noop — the user can still triple-click the command to select it
    }
  };

  return (
    <>
      <div
        style={{
          marginTop: "var(--space-xl)",
          background: "var(--color-surface)",
          borderRadius: "var(--radius-md)",
          boxShadow: "var(--shadow-card)",
          padding: "var(--space-lg)",
          borderLeft: "4px solid var(--color-info)",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--space-md)", marginBottom: "var(--space-md)" }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: "var(--font-size-md)", marginBottom: 2 }}>Add a new host</div>
            <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>SSH into a fresh Mac Mini, paste the command below, and the host is provisioned end-to-end.</div>
          </div>
          <button
            onClick={() => setShow(true)}
            style={{
              padding: "var(--space-sm) var(--space-md)",
              background: "transparent",
              color: "var(--color-info)",
              border: "1px solid var(--color-info)",
              borderRadius: "var(--radius-sm)",
              fontWeight: 500,
              fontSize: "var(--font-size-sm)",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Full setup guide →
          </button>
        </div>

        <div
          onClick={copyInstall}
          title="Click to copy"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-sm)",
            background: "var(--color-foreground)",
            border: "1px solid var(--color-border, rgba(0,0,0,0.08))",
            borderRadius: "var(--radius-sm)",
            padding: "10px 12px",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--font-size-sm)",
            cursor: "pointer",
            userSelect: "all",
          }}
        >
          <span style={{ color: "var(--color-text-secondary)", userSelect: "none" }}>$</span>
          <span style={{ flex: 1, overflow: "auto", whiteSpace: "nowrap" }}>{INSTALL_CMD}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              copyInstall();
            }}
            style={{
              padding: "4px 10px",
              background: copied ? "var(--color-success)" : "var(--color-primary)",
              color: "#fff",
              border: "none",
              borderRadius: "var(--radius-sm)",
              fontSize: "var(--font-size-xs)",
              fontWeight: 600,
              cursor: "pointer",
              flexShrink: 0,
              transition: "background 120ms",
            }}
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-secondary)", marginTop: 8 }}>
          The script will prompt for an agent API key — create one in <a href="/settings" style={{ color: "var(--color-info)" }}>Settings → API Keys</a> first.
        </div>
      </div>

      {show && (
        <div
          onClick={() => setShow(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "var(--color-surface)", borderRadius: "var(--radius-md)", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", width: "90%", maxWidth: 700, maxHeight: "85vh", overflow: "auto", padding: "var(--space-xl)" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-lg)" }}>
              <h2 style={{ fontSize: "var(--font-size-lg)", fontWeight: 700, color: "var(--color-primary)", margin: 0 }}>Mac Mini Host Setup</h2>
              <button onClick={() => setShow(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "var(--color-text-secondary)" }}>{"\u2715"}</button>
            </div>
            <div style={{ fontSize: "var(--font-size-sm)", lineHeight: 1.7 }}>
              <h3 style={{ color: "var(--color-primary)", marginTop: 0 }}>Prerequisites</h3>
              <ul style={{ paddingLeft: 20, color: "var(--color-text-secondary)" }}>
                <li>Mac Mini (Intel or Apple Silicon) with ethernet and static IP</li>
                <li>Powered USB hub (Anker/CalDigit recommended)</li>
                <li>Android phones with USB debugging enabled</li>
                <li>An agent API key (generate from Settings)</li>
              </ul>
              <h3 style={{ color: "var(--color-primary)" }}>Quick Setup (Automated)</h3>
              <p>SSH into the Mac Mini and run:</p>
              <pre style={{ background: "var(--color-foreground)", padding: "var(--space-md)", borderRadius: "var(--radius-sm)", fontFamily: "var(--font-mono)", fontSize: "var(--font-size-xs)", overflow: "auto" }}>
{`curl -fsSL ${INSTALL_URL} | bash`}
              </pre>
              <p style={{ color: "var(--color-text-secondary)" }}>No git clone needed. The script prompts for Device Hive URL, agent API key, and hostname — then installs dependencies, clones the monorepo, registers the host, and starts the agent daemon.</p>
              <p style={{ color: "var(--color-text-secondary)", fontSize: "var(--font-size-xs)" }}>
                <strong>If you already have the monorepo cloned</strong>, you can run <code>bash ops/device-hive/scripts/host-setup.sh</code> directly — same script.
              </p>
              <h3 style={{ color: "var(--color-primary)" }}>Phone Setup (Per Device)</h3>
              <ol style={{ paddingLeft: 20, color: "var(--color-text-secondary)" }}>
                <li>Enable Developer Mode: Settings → About Phone → tap Build Number 7 times</li>
                <li>Enable USB Debugging in Developer Options</li>
                <li>Enable "Disable adb authorization timeout" in Developer Options</li>
                <li>Enable "Stay awake" in Developer Options</li>
                <li>Plug in via USB — accept the "Allow USB debugging?" prompt</li>
                <li><strong>Samsung only:</strong> Set Default USB configuration → File transfer</li>
              </ol>
              <h3 style={{ color: "var(--color-primary)" }}>Troubleshooting</h3>
              <ul style={{ paddingLeft: 20, color: "var(--color-text-secondary)" }}>
                <li><strong>Phone not showing:</strong> Try a different cable (charge-only won't work)</li>
                <li><strong>"Unauthorized":</strong> Check phone screen for USB debugging trust prompt</li>
                <li><strong>"Offline":</strong> Unplug/replug, or reset ADB keys: <code style={{ background: "var(--color-foreground)", padding: "2px 4px", borderRadius: 3 }}>rm ~/.android/adbkey*</code></li>
                <li><strong>Samsung charging-only:</strong> Pull down notification → tap USB → File transfer</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function HostCard({
  host,
  onAction,
}: {
  host: Host;
  onAction: (id: string, action: "drain" | "remove") => void;
}) {
  const total = host.availableCount + host.busyCount + host.offlineCount;
  const pctAvailable = total > 0 ? (host.availableCount / total) * 100 : 0;
  const pctBusy = total > 0 ? (host.busyCount / total) * 100 : 0;
  const pctOffline = total > 0 ? (host.offlineCount / total) * 100 : 0;

  const statusDotColor =
    host.status === "online"
      ? "var(--color-success)"
      : host.status === "draining"
        ? "var(--color-warning)"
        : "var(--color-danger)";

  return (
    <div
      style={{
        background: "var(--color-surface)",
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-card)",
        padding: "var(--space-lg)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "var(--space-md)",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)" }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: statusDotColor,
                display: "inline-block",
              }}
            />
            <a
              href={`/hosts/${host.id}`}
              onClick={(e) => { e.stopPropagation(); }}
              style={{ fontSize: "var(--font-size-md)", fontWeight: 600, color: "var(--color-primary)", textDecoration: "none" }}
            >
              {host.hostname}
            </a>
          </div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--font-size-xs)",
              color: "var(--color-text-secondary)",
              marginTop: "var(--space-xs)",
            }}
          >
            {host.ip}
          </div>
        </div>
        <div
          style={{
            fontSize: "var(--font-size-sm)",
            color: "var(--color-text-secondary)",
          }}
        >
          {host.deviceCount} devices
        </div>
      </div>

      {/* Utilization Bar */}
      <div
        style={{
          height: 8,
          borderRadius: 4,
          background: "var(--color-foreground)",
          display: "flex",
          overflow: "hidden",
          marginBottom: "var(--space-md)",
        }}
      >
        <div style={{ width: `${pctAvailable}%`, background: "var(--color-success)" }} />
        <div style={{ width: `${pctBusy}%`, background: "var(--color-warning)" }} />
        <div style={{ width: `${pctOffline}%`, background: "var(--color-danger)" }} />
      </div>

      {/* Counts */}
      <div
        style={{
          display: "flex",
          gap: "var(--space-md)",
          fontSize: "var(--font-size-sm)",
          color: "var(--color-text-secondary)",
          marginBottom: "var(--space-md)",
        }}
      >
        <span>
          <span style={{ color: "var(--color-success)" }}>{host.availableCount}</span> available
        </span>
        <span>
          <span style={{ color: "var(--color-warning)" }}>{host.busyCount}</span> busy
        </span>
        <span>
          <span style={{ color: "var(--color-danger)" }}>{host.offlineCount}</span> offline
        </span>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: "var(--space-sm)" }}>
        {host.status === "online" && (
          <button
            onClick={() => onAction(host.id, "drain")}
            style={{ ...smallButtonStyle, color: "var(--color-warning)" }}
          >
            Drain
          </button>
        )}
        <button
          onClick={() => onAction(host.id, "remove")}
          style={{ ...smallButtonStyle, color: "var(--color-danger)" }}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

const pageTitleStyle: React.CSSProperties = {
  fontSize: "var(--font-size-xl)",
  fontWeight: 700,
  color: "var(--color-primary)",
  margin: 0,
};

const buttonStyle: React.CSSProperties = {
  padding: "var(--space-sm) var(--space-lg)",
  border: "none",
  borderRadius: "var(--radius-sm)",
  background: "var(--color-primary)",
  color: "var(--color-surface)",
  fontSize: "var(--font-size-base)",
  fontWeight: 600,
  cursor: "pointer",
  minHeight: "var(--touch-target-min)",
};

const smallButtonStyle: React.CSSProperties = {
  padding: "var(--space-xs) var(--space-sm)",
  border: "var(--border-default)",
  borderRadius: "var(--radius-sm)",
  background: "var(--color-surface)",
  fontSize: "var(--font-size-sm)",
  fontWeight: 600,
  cursor: "pointer",
};

const labelStyle: React.CSSProperties = {
  fontSize: "var(--font-size-sm)",
  fontWeight: 600,
  color: "var(--color-text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const inputStyle: React.CSSProperties = {
  padding: "var(--space-sm) var(--space-md)",
  border: "var(--border-default)",
  borderRadius: "var(--radius-sm)",
  background: "var(--color-surface)",
  fontSize: "var(--font-size-base)",
  minHeight: "var(--touch-target-min)",
};
